/**
 * Primary app sidebar: expanded (250px) or collapsed (75px); supports master-access (no dashboard links).
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { useMediaQuery } from "react-responsive";
import PropTypes from "prop-types";

import useAuthStore from "store/useAuthStore";
import useNavState from "store/useNavState";
import useProForma from "store/useProForma";
import {
  fetchMasterAccessData,
  fetchMasterAccessByCode,
} from "api/masteraccessapi";

import NavItem from "./navbar/NavItem";
import CityAccessListSection from "./navbar/CityAccessListSection";
import NavbarBottomPromos from "./navbar/NavbarBottomPromos";
import NavbarUserMenu from "./navbar/NavbarUserMenu";
import MasterAccessLogoutFooter from "./navbar/MasterAccessLogoutFooter";
import { NAV_ITEMS } from "./navbar/navConfig";

const Navbar = ({ showLinks = true }) => {
  const { logout, user } = useAuthStore();
  const { isOpen, toggleNav, setNav } = useNavState();
  const {
    cityInfo,
    parentData,
    setParentData,
    cityAccess,
    setCityAccess,
    cityNameByUsername,
  } = useProForma();

  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();

  const [username, setUsername] = useState("");
  const [cityAccessList, setCityAccessList] = useState(null);
  const [screenHeight, setScreenHeight] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 0,
  );

  const { code } = location.state || {};
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1023px)" });

  useEffect(() => {
    const onResize = () => setScreenHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (isTabletOrMobile) setNav(false);
  }, [isTabletOrMobile, setNav]);

  useEffect(() => {
    if (!code) return;
    (async () => {
      const data = await fetchMasterAccessByCode(code);
      if (data?.error) {
        setCityAccess(null);
        setCityAccessList(null);
        return;
      }
      setCityAccess(data.cityAccess);
      setCityAccessList(data.cityAccessList ?? null);
    })();
  }, [code, setCityAccess]);

  useEffect(() => {
    if (!user) return;
    setUsername(user.email.split("@")[0]);
  }, [user]);

  useEffect(() => {
    if (!username) return;
    (async () => {
      const data = await fetchMasterAccessData(username);
      if (data && !data.error) setParentData(data);
      else setParentData(null);
    })();
  }, [username, setParentData]);

  const handleLogout = async () => {
    posthog?.capture("User logout");
    await logout();
    navigate("/login");
  };

  return (
    <>
      {isOpen ? (
        <div className="h-screen w-[250px] fixed bg-background_gray">
          <div className="flex items-center justify-between h-8 px-5 mt-4">
            <img
              src="/Optony_Logo_Name_Black 1.svg"
              alt="Optony"
              className="h-8"
            />
          </div>
          <div className="w-full pl-[53px] pt-1 text-[#8a8d89] text-base font-normal font-['Roboto'] leading-tight">
            Pro Forma
          </div>

          <nav className="p-5 mt-4">
            <CityAccessListSection
              cityAccessList={cityAccessList}
              accessCode={code}
              cityNameByUsername={cityNameByUsername}
            />
            {showLinks && (
              <ul>
                {NAV_ITEMS.map((item) => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    isOpen
                  />
                ))}
              </ul>
            )}
          </nav>

          <div className="w-full flex flex-col justify-center p-4 absolute bottom-0 items-center">
            <NavbarBottomPromos
              pathname={location.pathname}
              screenHeight={screenHeight}
              parentData={parentData}
              cityAccess={cityAccess}
            />
            {showLinks ? (
              <NavbarUserMenu
                variant="expanded"
                cityInfo={cityInfo}
                username={username}
                onLogout={handleLogout}
              />
            ) : (
              <MasterAccessLogoutFooter collapsed={false} />
            )}
          </div>
        </div>
      ) : (
        <div className="h-screen w-[75px] fixed bg-background_gray">
          <div className="flex items-center justify-left h-8 pl-5 mt-4">
            <button
              type="button"
              onClick={toggleNav}
              aria-label="Expand navigation">
              <img
                src="icons/collapsedLogo.svg"
                alt=""
              />
            </button>
          </div>
          <nav className="p-2 mt-4 mx-auto flex flex-col items-center">
            {showLinks && (
              <ul>
                {NAV_ITEMS.map((item) => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    isOpen={false}
                  />
                ))}
              </ul>
            )}
          </nav>
          {showLinks ? (
            <NavbarUserMenu
              variant="collapsed"
              cityInfo={cityInfo}
              username={username}
              onLogout={handleLogout}
            />
          ) : (
            <MasterAccessLogoutFooter collapsed />
          )}
        </div>
      )}
    </>
  );
};

Navbar.propTypes = {
  showLinks: PropTypes.bool,
};

export default Navbar;
